import type { Polygon, Position } from "geojson";
import { fx_clienteMapaRegistro } from "./MapaRegistroFinca";

/** Los vértices de UNA pieza del paso 3, leídos de la base.
 *
 *  **Qué reemplaza.** Antes el modo Editar leía la GEOMETRÍA de la pieza
 *  (`PoligonosPadreWfsService.leerAsync`) y la partía en vértices en el
 *  navegador. Ahora los vértices son datos: filas de
 *  `registro_finca.tbl_temp_vertices_dibujo` que el servidor extrae al guardar
 *  el área, con su `orden_vertice`. Se piden por el id de la pieza y se usan
 *  tal como vienen.
 *
 *  **Va por gRPC** (`LeerVerticesPieza`) desde el 2026-10-11. Antes era un
 *  GetFeature de WFS directo a GeoServer filtrado solo por el id de la pieza;
 *  ahora el servidor comprueba además que el registro sea del cliente. El
 *  nombre de la clase quedó de cuando iba por WFS.
 *
 *  **Y es la MISMA fuente que dibuja la capa WMS de vértices.** Eso es lo que
 *  hace que los handles caigan exactamente sobre los puntos que el usuario ve:
 *  cuando el que pinta y el que se edita salen de dos lugares distintos,
 *  alcanza un redondeo para que no coincidan.
 *
 *  **Los vértices NO se renderizan en Leaflet.** Quedan en memoria y de ahí
 *  salen los tres handles que `AoiSnapEditEngine` pone al vuelo bajo el cursor
 *  (los dos extremos del tramo y su punto medio). Los puntos los dibuja el WMS;
 *  Leaflet sólo dibuja lo que se puede agarrar.
 *
 *  **Es de SOLO LECTURA.** Escribir un vértice por otro camino dejaría dos
 *  escritores sobre la misma geometría: el polígono vive además en
 *  `tbl_temp_crear_finca.poligonos_padre`, y es el `GuardarAoi` el que valida
 *  que la forma no se cruce consigo misma ni pise otra pieza. El camino es: el
 *  vértice se mueve → `GuardarAoi` → el servidor re-extrae → se repinta la
 *  capa.
 */
export class VerticesPoligonoWfsService {
  /** Los vértices de la pieza, en el orden del anillo, o `null` si el servidor
   *  no contestó o la pieza no tiene vértices guardados.
   *
   *  `null` y no un arreglo vacío a propósito: son dos cosas distintas para el
   *  llamador. Sin vértices no hay nada que editar y hay que caer en la
   *  geometría del modelo (el camino degradado); un arreglo vacío se leería
   *  como "esta pieza no tiene esquinas", que no existe. */
  async leerAsync(
    registroId: string | null,
    poligonoPadreId: string,
  ): Promise<Position[] | null> {
    if (!registroId || !poligonoPadreId) return null;

    try {
      // El orden lo resuelve el servidor (`orden_vertice`): es el dato que
      // vuelve a armar el anillo.
      const respuesta = await fx_clienteMapaRegistro().leerVerticesPieza({
        registroId,
        poligonoPadreId,
      });
      const puntos: Position[] = respuesta.vertices.map((v) => [v.lng, v.lat]);

      // Con menos de 3 no hay anillo: es una pieza a medio extraer (un guardado
      // en vuelo, por ejemplo). El servidor ya los manda vacíos en ese caso; se
      // vuelve a mirar acá para no entregarle al motor de snap un anillo
      // imposible.
      return puntos.length >= 3 ? puntos : null;
    } catch (error) {
      // Sin respuesta el modo Editar sigue andando contra la geometría del
      // modelo. Es un camino degradado, no un error que el usuario tenga que
      // ver: lo que se pierde es que los handles salgan de la base.
      console.error("No se pudieron leer los vértices de la pieza", error);
      return null;
    }
  }

  /** Los vértices leídos como el `Polygon` que espera el motor de snap.
   *
   *  El anillo se CIERRA acá: la tabla no guarda el punto de cierre (sería el
   *  vértice 1 repetido) y GeoJSON lo exige. Guardarlo habría hecho que el modo
   *  Editar mostrara dos handles superpuestos en la misma esquina. */
  static aPoligono(vertices: Position[]): Polygon {
    return {
      type: "Polygon",
      coordinates: [[...vertices, vertices[0]]],
    };
  }
}
